import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES_SEANCE = [
    "Portrait individuel — studio",
    "Portrait couple — studio",
    "Portrait famille — studio",
    "Portrait enfant — studio",
    "Portrait maternité — studio",
    "Portrait extérieur",
    "Famille / groupe — extérieur",
    "Animaux & chevaux",
    "Autre",
];

const EMPLACEMENTS = ["Studio", "Extérieur", "À discuter"];

const inputCls =
    "w-full border border-[#D4AF37]/25 bg-[#0B0B0C] px-4 py-3 text-sm font-light text-white outline-none transition-colors duration-300 placeholder:text-[#8A8A8E]/60 focus:border-[#D4AF37]";
const labelCls =
    "font-mono-lux mb-2 block text-[9px] tracking-[0.3em] text-[#D4AF37]";

const EMPTY = {
    nom: "",
    courriel: "",
    telephone: "",
    type_seance: "",
    emplacement: "",
    lieu: "",
    date_souhaitee: "",
    message: "",
};

export default function SeanceForm({ open, onClose }) {
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState("idle");

    useEffect(() => {
        document.documentElement.style.overflow = open ? "hidden" : "";
        return () => {
            document.documentElement.style.overflow = "";
        };
    }, [open]);

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            await axios.post(`${API}/seance-request`, form);
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    const close = () => {
        onClose();
        setTimeout(() => {
            setForm(EMPTY);
            setStatus("idle");
        }, 400);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="seance-form-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                >
                    <div
                        className="absolute inset-0 bg-[#050505]/92 backdrop-blur-md"
                        onClick={close}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.98 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto border border-[#D4AF37]/30 bg-[#0B0B0C] p-7 shadow-[0_30px_80px_rgba(0,0,0,0.9)] sm:p-10"
                    >
                        <span className="pointer-events-none absolute left-0 top-0 h-7 w-7 border-l-2 border-t-2 border-[#D4AF37]" />
                        <span className="pointer-events-none absolute right-0 top-0 h-7 w-7 border-r-2 border-t-2 border-[#D4AF37]" />
                        <span className="pointer-events-none absolute bottom-0 left-0 h-7 w-7 border-b-2 border-l-2 border-[#D4AF37]" />
                        <span className="pointer-events-none absolute bottom-0 right-0 h-7 w-7 border-b-2 border-r-2 border-[#D4AF37]" />

                        <button
                            type="button"
                            data-testid="seance-close-button"
                            onClick={close}
                            aria-label="Fermer le formulaire"
                            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10"
                        >
                            <X size={17} />
                        </button>

                        {status === "success" ? (
                            <div className="flex flex-col items-center gap-6 py-14 text-center">
                                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37]">
                                    <Check size={26} strokeWidth={1.4} />
                                </span>
                                <p
                                    data-testid="seance-success-message"
                                    className="font-display max-w-md text-xl font-light italic leading-relaxed text-[#E2E2E2] sm:text-2xl"
                                >
                                    « Merci pour votre demande! Je vous
                                    contacterai rapidement afin de discuter de
                                    votre séance et de confirmer les détails. »
                                </p>
                                <button
                                    type="button"
                                    data-testid="seance-success-close-button"
                                    onClick={close}
                                    className="font-mono-lux mt-2 border border-[#D4AF37]/40 px-8 py-3 text-[10px] tracking-[0.3em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10"
                                >
                                    FERMER
                                </button>
                            </div>
                        ) : (
                            <>
                                <p className="font-mono-lux mb-3 text-[10px] tracking-[0.4em] text-[#D4AF37]">
                                    TARIFS & SÉANCES
                                </p>
                                <h3 className="font-display mb-8 pr-10 text-2xl font-medium tracking-tight text-white sm:text-3xl">
                                    VOTRE{" "}
                                    <span className="text-gold-grad italic">
                                        DEMANDE
                                    </span>{" "}
                                    DE SÉANCE
                                </h3>

                                <form
                                    onSubmit={submit}
                                    className="grid gap-5 sm:grid-cols-2"
                                >
                                    <div>
                                        <label className={labelCls}>
                                            NOM ET PRÉNOM *
                                        </label>
                                        <input
                                            data-testid="seance-input-nom"
                                            required
                                            value={form.nom}
                                            onChange={set("nom")}
                                            className={inputCls}
                                            placeholder="Votre nom complet"
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>
                                            COURRIEL *
                                        </label>
                                        <input
                                            data-testid="seance-input-courriel"
                                            required
                                            type="email"
                                            value={form.courriel}
                                            onChange={set("courriel")}
                                            className={inputCls}
                                            placeholder="votre@courriel.com"
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>
                                            TÉLÉPHONE
                                        </label>
                                        <input
                                            data-testid="seance-input-telephone"
                                            type="tel"
                                            value={form.telephone}
                                            onChange={set("telephone")}
                                            className={inputCls}
                                            placeholder="514 000-0000"
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>
                                            TYPE DE SÉANCE *
                                        </label>
                                        <select
                                            data-testid="seance-select-type"
                                            required
                                            value={form.type_seance}
                                            onChange={set("type_seance")}
                                            className={`${inputCls} appearance-none`}
                                        >
                                            <option value="" disabled>
                                                Choisir…
                                            </option>
                                            {TYPES_SEANCE.map((t) => (
                                                <option key={t} value={t}>
                                                    {t}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>
                                            STUDIO OU EXTÉRIEUR
                                        </label>
                                        <select
                                            data-testid="seance-select-emplacement"
                                            value={form.emplacement}
                                            onChange={set("emplacement")}
                                            className={`${inputCls} appearance-none`}
                                        >
                                            <option value="" disabled>
                                                Choisir…
                                            </option>
                                            {EMPLACEMENTS.map((t) => (
                                                <option key={t} value={t}>
                                                    {t}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>
                                            LIEU SOUHAITÉ (SÉANCE EXTÉRIEURE)
                                        </label>
                                        <input
                                            data-testid="seance-input-lieu"
                                            value={form.lieu}
                                            onChange={set("lieu")}
                                            className={inputCls}
                                            placeholder="Parc, plage, ville…"
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>
                                            DATE SOUHAITÉE
                                        </label>
                                        <input
                                            data-testid="seance-input-date"
                                            type="date"
                                            value={form.date_souhaitee}
                                            onChange={set("date_souhaitee")}
                                            style={{ colorScheme: "dark" }}
                                            className={inputCls}
                                        />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className={labelCls}>
                                            MESSAGE / DÉTAILS DU PROJET
                                        </label>
                                        <textarea
                                            data-testid="seance-textarea-message"
                                            rows={4}
                                            value={form.message}
                                            onChange={set("message")}
                                            className={`${inputCls} resize-none`}
                                            placeholder="Parlez-moi de votre projet…"
                                        />
                                    </div>

                                    {status === "error" && (
                                        <p
                                            data-testid="seance-error-message"
                                            className="text-sm font-light text-red-400 sm:col-span-2"
                                        >
                                            Une erreur est survenue. Réessayez
                                            ou écrivez-moi directement à
                                            photographedanieldulude@gmail.com
                                        </p>
                                    )}

                                    <div className="sm:col-span-2">
                                        <button
                                            type="submit"
                                            data-testid="seance-submit-button"
                                            disabled={status === "sending"}
                                            className="group inline-flex w-full items-center justify-center gap-3 border border-[#D4AF37] bg-gradient-to-r from-[#9A7B38] via-[#D4AF37] to-[#9A7B38] px-8 py-4 font-mono-lux text-[10px] tracking-[0.28em] text-[#050505] transition-all duration-500 hover:shadow-[0_0_45px_rgba(212,175,55,0.35)] disabled:cursor-wait disabled:opacity-60 sm:text-xs"
                                        >
                                            {status === "sending"
                                                ? "ENVOI EN COURS…"
                                                : "ENVOYER MA DEMANDE"}
                                            <ArrowUpRight
                                                size={16}
                                                strokeWidth={1.6}
                                                className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                            />
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
